import { CustomerPhotoPanel } from './CustomerPhotoPanel';
import { ChecklistVariantWarning } from './ChecklistVariantWarning';
/**
 * Catalog detail page. Loads a single card via `GET /cards/{id}` and renders
 * its verification status, authoritative identity/grade (only when verified),
 * and the captured image via the presigned URL the API minted (Requirement 5.3).
 *
 * Under physical-first-card-tracking a card may be `UNIDENTIFIED`: in that state
 * the authoritative identity/grade are absent and review-only suggestions may
 * pre-fill the operator's identity form without being presented as verified
 * facts (Requirements 7.1, 7.2, 7.3, 7.4, 7.5).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { hasAnyGroup } from '../auth/cognito';
import { CopyIdButton } from '../shared/CopyIdButton';
import { EMPTY_IDENTITY, IdentityForm } from '../shared/IdentityForm';
import { shortDateTimeWithYear } from '../shared/datetime';
import { formatIdentity, IdentityStatusBadge } from '../shared/PredictionView';
import { describePlacement, placementAction } from '../storage/placement';
import { entityDetailHref } from '../storage/container-listing';
import { LabelTile } from '../storage/LabelTile';
import { printSingleLabel } from '../storage/label-print';
import { useStorageApi, type CardLabelResponse } from '../storage/storage-api';
import type {
  CardIdentity,
  ChecklistEntry,
  ChecklistParallelDefinition,
  ChecklistParallelMatch,
  ImageQualityStatus,
  OcrMetadata,
  ChecklistSourceSelection,
  OcrSideResult,
  OcrTextLine,
} from '../shared/types';
import {
  useCatalogApi,
  type CatalogDetailResponse,
  type ChecklistResolveGroup,
  type IdentityOnlyReviewRequest,
  identityReviewResponseMatches,
} from './catalog-api';
import { CardResearchPricing } from './CardResearchPricing';
import { useVersionedImageReview, VersionedImageReviewStatus } from './useVersionedImageReview';
import { useChecklistSearch } from '../checklists/useChecklistSearch';
import { ChecklistParallelFallback } from './ChecklistParallelFallback';
import { ChecklistSelectionChanges } from '../batches/ChecklistSelectionChanges';
import { batchReviewHref } from '../batches/review-navigation';
import {
  applicableParallelDefinitions,
  checklistSelectionSource,
  checklistSelectionConflictMessages,
  parallelSelectionOptions,
  checklistRosterSubset,
  checklistPlainChoiceAllowed,
  parallelDefinitionLabel,
  reviewedIdentityFromChecklist,
} from './checklist-parallel-selection';

const DETAIL_CHECKLIST_SELECTION = { allowUnverifiedSubsetSelection: true } as const;

const isReviewDigest = (value: unknown): value is string =>
  typeof value === 'string' && /^[0-9a-f]{64}$/.test(value);

export function CatalogDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  return <CatalogDetailVisit key={id} />;
}
function CatalogDetailVisit(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const catalog = useCatalogApi(),
    storage = useStorageApi();
  const visit = useRef({ id });
  const alive = useRef(true);
  const reads = useRef(0),
    resolves = useRef(0),
    edits = useRef(0),
    saves = useRef(0);
  const saveLatch = useRef<object | null>(null);
  const current = useRef<CatalogDetailResponse | null>(null);
  const dirty = useRef(false);
  const [card, setCard] = useState<CatalogDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<CardIdentity | null>(null);
  const [saving, setSaving] = useState(false);
  const [mustReload, setMustReload] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [manual, setManual] = useState(false);
  const [reason, setReason] = useState('');
  const [completed, setCompleted] = useState(false);
  const [includeEvidence, setIncludeEvidence] = useState(false);
  const [replaceAssignment, setReplaceAssignment] = useState(false);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [identityAccepted, setIdentityAccepted] = useState(false);
  type Choice = {
    kind: 'SOURCE' | 'MANUAL' | 'RETAIN';
    identity: CardIdentity;
    source?: ChecklistSourceSelection;
    reason?: string;
    completed?: boolean;
    evidence?: ChecklistSourceSelection;
  };
  type Proposal = Choice & {
    visit: object;
    digest: string | undefined;
    before: string;
    edit: number;
  };
  const [pendingIdentity, setPendingIdentity] = useState<Proposal | null>(null);
  const [accepted, setAccepted] = useState<(Choice & { digest: string; local: string }) | null>(
    null,
  );
  const [candidates, setCandidates] = useState<ChecklistEntry[]>([]);
  const [parallelDefinitions, setParallelDefinitions] = useState<ChecklistParallelDefinition[]>([]);
  const [parallelMatch, setParallelMatch] = useState<ChecklistParallelMatch | undefined>();
  const [candidateGroups, setCandidateGroups] = useState<ChecklistResolveGroup[]>([]);
  const [label, setLabel] = useState<CardLabelResponse | null>(null);
  const [productQuery, setProductQuery] = useState('');
  const [submittedProductQuery, setSubmittedProductQuery] = useState<string | null>(null);
  const productSearch = useChecklistSearch(catalog.reviewChecklists, submittedProductQuery ?? '', {
    enabled: submittedProductQuery !== null,
    limit: 25,
  });
  const { items: products, setItems: setProducts } = productSearch;
  const [sourceBusy, setSourceBusy] = useState(false);
  const [productSelection, setProductSelection] = useState('');
  const [printedNumber, setPrintedNumber] = useState('');
  const explicitLookupOwner = useRef<number | null>(null);
  const { claims } = useAuth();
  const canEdit = claims !== null && hasAnyGroup(claims, ['reviewer', 'admin']);
  const isAdmin = claims !== null && hasAnyGroup(claims, ['admin']);
  const variantOwnerKey = JSON.stringify([id, claims?.sub, card?.identityReviewDigest]);
  const variantOwner = useRef({ key: variantOwnerKey, catalog, epoch: 0 });
  if (variantOwner.current.key !== variantOwnerKey || variantOwner.current.catalog !== catalog)
    variantOwner.current = { key: variantOwnerKey, catalog, epoch: variantOwner.current.epoch + 1 };
  const [variantObservation, setVariantObservation] = useState<{
    owner: number;
    request: number;
    checklistId: string;
    rows: ChecklistEntry[];
    definitions: ChecklistParallelDefinition[];
  } | null>(null);
  const [variantChoice, setVariantChoice] = useState<{
    entry: ChecklistEntry;
    source: ChecklistSourceSelection;
  } | null>(null);

  const imageReview = useVersionedImageReview(
    canEdit && card?.cardId === id ? card : null,
    catalog,
    'FINAL',
  );
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      reads.current++;
      resolves.current++;
      saves.current++;
    };
  }, []);
  const invalidate = () => {
    setVariantChoice(null);
    setAccepted(null);
    setPendingIdentity(null);
    setIdentityAccepted(false);
  };
  const clearSourceChoices = useCallback(() => {
    setSubmittedProductQuery(null);
    const token = ++resolves.current;
    setSourceBusy(false);
    setCandidates([]);
    setCandidateGroups([]);
    setParallelDefinitions([]);
    setParallelMatch(undefined);
    setVariantObservation(null);
    setVariantChoice(null);
    setAccepted(null);
    setPendingIdentity(null);
    setIdentityAccepted(false);
    setSelectionError(null);
    return token;
  }, []);
  const sourceEpoch = variantOwner.current.epoch;
  useEffect(() => {
    setSubmittedProductQuery(null);
    if (explicitLookupOwner.current !== null && explicitLookupOwner.current !== sourceEpoch) {
      explicitLookupOwner.current = null;
      clearSourceChoices();
    }
  }, [sourceEpoch, clearSourceChoices]);
  const changeDraft = (value: CardIdentity) => {
    edits.current++;
    dirty.current = true;
    setDraft(value);
    setCompleted(false);
    invalidate();
  };
  const load = useCallback(
    async (explicit = false): Promise<boolean> => {
      if (id === undefined || !alive.current) return false;
      if (explicit) clearSourceChoices();
      const at = visit.current,
        token = ++reads.current,
        edit = edits.current;
      try {
        const result = await catalog.get(id);
        if (!alive.current || visit.current !== at || reads.current !== token) return false;
        if (!result.ok || result.data.cardId !== id) {
          const message = 'Unable to reload this card. Your local review is still here.';
          if (current.current) setActionError(message);
          else setError('Unable to load this card.');
          return false;
        }
        const previous = current.current;
        current.current = result.data;
        setCard(result.data);
        setError(null);
        if (previous?.identityReviewDigest !== result.data.identityReviewDigest) {
          setSubmittedProductQuery(null);
          resolves.current++;
          setSourceBusy(false);
          resolvedFor.current = null;
          setCandidates([]);
          setCandidateGroups([]);
          setParallelDefinitions([]);
          setCompleted(false);
          setIncludeEvidence(false);
          setAccepted(null);
          setPendingIdentity(null);
          setIdentityAccepted(false);
        }
        if (!dirty.current && edits.current === edit) setDraft(initialDraft(result.data));
        if (explicit) {
          resolvedFor.current = null;
          resolves.current++;
          setSourceBusy(false);
          setCandidates([]);
          setCandidateGroups([]);
          setParallelDefinitions([]);
          setMustReload(false);
          setUncertain(false);
          setActionError(null);
          setAccepted(null);
          setPendingIdentity(null);
        }
        return true;
      } catch {
        if (alive.current && visit.current === at && reads.current === token) {
          if (current.current)
            setActionError('Unable to reload this card. Your local review is still here.');
          else setError('Unable to load this card.');
        }
        return false;
      }
    },
    [catalog, id, clearSourceChoices],
  );
  const refresh = useCallback(async () => {
    await load();
  }, [load]);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (id === undefined) return;
    const at = visit.current;
    void storage
      .labels('cards', id)
      .then((result) => {
        if (
          alive.current &&
          visit.current === at &&
          result.ok &&
          (result.data as CardLabelResponse).cardId === id
        )
          setLabel(result.data as CardLabelResponse);
      })
      .catch(() => {});
  }, [storage, id]);
  const resolvedFor = useRef<object | null>(null);
  const candidateGeneration = useRef(0);
  const browsedChecklist = useRef<string | undefined>();
  const browse = useCallback(
    async (checklistId: string, searchNumber?: string) => {
      if (!id || !checklistId || !alive.current) return;
      const at = visit.current,
        token = clearSourceChoices();
      const variantEpoch = variantOwner.current.epoch;
      explicitLookupOwner.current = searchNumber === undefined ? null : variantEpoch;
      browsedChecklist.current = checklistId;
      setProductSelection(checklistId);
      setSourceBusy(true);
      try {
        const result = await catalog.checklistResolve(id, {
          tentativeChecklist: true,
          checklistId,
          ...(searchNumber === undefined ? {} : { searchNumber }),
        });
        if (
          !alive.current ||
          visit.current !== at ||
          resolves.current !== token ||
          (searchNumber !== undefined && variantOwner.current.epoch !== variantEpoch)
        )
          return;
        if (!result.ok) {
          setSelectionError('Checklist choices are unavailable. Your local identity is unchanged.');
          return;
        }
        const match = result.data.match;
        const rows =
          result.data.candidates ?? (match && 'identity' in match ? [match as ChecklistEntry] : []);
        const allRows = [
          ...rows,
          ...(result.data.groups ?? []).flatMap((group) => group.candidates),
        ];
        if (
          allRows.some(
            (entry) =>
              !entry.source ||
              `SOURCE#${entry.source.sourceSystem}#${entry.source.sourceTargetId}` !== checklistId,
          )
        ) {
          setSelectionError(
            'Checklist choices did not match the selected product. Your local identity is unchanged.',
          );
          return;
        }
        if (variantOwner.current.epoch === variantEpoch)
          setVariantObservation({
            owner: variantEpoch,
            request: token,
            checklistId,
            rows: allRows,
            definitions: result.data.parallelDefinitions ?? [],
          });
        if (searchNumber !== undefined && allRows.length === 0)
          setSelectionError(
            'No current source row matched this printed number in the selected product.',
          );
        candidateGeneration.current = token;
        setParallelDefinitions(result.data.parallelDefinitions ?? []);
        setParallelMatch(result.data.parallelMatch);
        setCandidateGroups(result.data.groups ?? []);
        setCandidates(rows);
      } catch {
        if (alive.current && visit.current === at && resolves.current === token)
          setSelectionError('Checklist choices are unavailable. Your local identity is unchanged.');
      } finally {
        if (alive.current && visit.current === at && resolves.current === token)
          setSourceBusy(false);
      }
    },
    [catalog, id, clearSourceChoices],
  );
  useEffect(() => {
    if (!card || card.cardId !== id || resolvedFor.current === visit.current) return;
    resolvedFor.current = visit.current;
    const checklistId =
      card.reviewChecklistSelection?.checklistId ??
      card.checklistSourceLineage?.checklistId ??
      card.identityReviewDraft?.checklistSourceSelection?.checklistId ??
      browsedChecklist.current;
    // Tentative resolve requires an exact selected product and performs no assignment writes.
    if (checklistId) void browse(checklistId);
  }, [card, browse, id]);
  const lookupPrintedNumber = () => {
    const number = printedNumber.trim();
    // This is a deliberate search hint, never saved/draft identity evidence.
    clearSourceChoices();
    if (
      !productSelection ||
      !number ||
      number.length > 32 ||
      [...number].some(
        (character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127,
      )
    ) {
      setSelectionError('Select a product and enter a printed card number of 1–32 characters.');
      return;
    }
    void browse(productSelection, number);
  };
  const findProducts = () => {
    if (!id || !alive.current) return;
    clearSourceChoices();
    setProductSelection('');
    browsedChecklist.current = undefined;
    setPrintedNumber('');
    setProducts([]);
    setSubmittedProductQuery(productQuery.trim());
    productSearch.refresh();
  };
  const before = () =>
    JSON.stringify([
      card?.identityReviewDigest,
      card?.identity,
      card?.identityReviewDraft,
      draft,
      reason,
      completed,
      includeEvidence,
    ]);
  const stage = (choice: Choice) => {
    if (!card || saving || mustReload || uncertain) return;
    setPendingIdentity({
      ...choice,
      visit: visit.current,
      digest: card.identityReviewDigest,
      before: before(),
      edit: edits.current,
    });
    setIdentityAccepted(false);
    setReplaceAssignment(false);
    setAccepted(null);
    setSelectionError(null);
  };
  const stageSource = (entry: ChecklistEntry, definition?: ChecklistParallelDefinition) => {
    if (!card || !entry.source || candidateGeneration.current !== resolves.current) return;
    const checklistId = `SOURCE#${entry.source.sourceSystem}#${entry.source.sourceTargetId}`;
    if (checklistId !== browsedChecklist.current) return;
    const identity = reviewedIdentityFromChecklist(
      entry,
      definition,
      { card, checklistId },
      DETAIL_CHECKLIST_SELECTION,
    );
    const source = checklistSelectionSource(checklistId, entry, definition);
    if (!identity || !source) {
      setSelectionError(
        'This choice cannot verify an exact current source. Keep researching or choose explicit manual review.',
      );
      return;
    }
    if (
      variantObservation?.owner === variantOwner.current.epoch &&
      variantObservation.request === resolves.current &&
      variantObservation.rows.includes(entry)
    )
      setVariantChoice({ entry, source });
    else setVariantChoice(null);
    stage({ kind: 'SOURCE', identity, source });
  };
  const enterManual = () => {
    if (saving) return;
    dirty.current = true;
    setEditing(true);
    setManual(true);
    setCompleted(false);
    invalidate();
  };
  const evidence =
    card?.identityReviewDraft?.manualIdentityOverrideSourceEvidence ??
    card?.manualIdentityOverride?.sourceEvidence;
  const pendingRequiresReplacement =
    !!card?.reviewChecklistSelection &&
    !!pendingIdentity &&
    pendingIdentity.kind !== 'RETAIN' &&
    (pendingIdentity.kind === 'MANUAL' ||
      pendingIdentity.source?.checklistId !== card.reviewChecklistSelection.checklistId);
  const proposalCurrent =
    !!pendingIdentity &&
    pendingIdentity.visit === visit.current &&
    pendingIdentity.digest === card?.identityReviewDigest &&
    pendingIdentity.before === before() &&
    pendingIdentity.edit === edits.current;
  const variantSource =
    pendingIdentity?.kind === 'SOURCE' && proposalCurrent
      ? pendingIdentity.source
      : accepted?.kind === 'SOURCE' && accepted.local === JSON.stringify(draft)
        ? accepted.source
        : undefined;
  const showVariant =
    !sourceBusy &&
    !saving &&
    !mustReload &&
    !uncertain &&
    !manual &&
    variantObservation?.owner === variantOwner.current.epoch &&
    variantObservation.request === resolves.current &&
    variantChoice !== null &&
    JSON.stringify(variantChoice.source) === JSON.stringify(variantSource);
  const variantAssociation = {
    cardId: id ?? '',
    visitId: String(variantOwner.current.epoch),
    requestId: String(resolves.current),
    checklistId: browsedChecklist.current ?? '',
    printedNumber: variantChoice?.entry.identity.number ?? '',
  };
  const onSave = async () => {
    if (
      !id ||
      !card ||
      !accepted ||
      saving ||
      saveLatch.current ||
      mustReload ||
      uncertain ||
      !isReviewDigest(card.identityReviewDigest) ||
      accepted.digest !== card.identityReviewDigest ||
      accepted.local !== JSON.stringify(draft)
    )
      return;
    const at = visit.current,
      token = ++saves.current,
      latch = {};
    saveLatch.current = latch;
    setSaving(true);
    setMustReload(true);
    setAccepted(null);
    setActionError(null);
    setNotice(null);
    const read = reads.current,
      edit = edits.current,
      savedCard = card;
    const base = {
      reviewScope: 'IDENTITY_ONLY' as const,
      mode: 'VERIFY' as const,
      expectedIdentityReviewDigest: accepted.digest,
      identity: accepted.identity,
    };
    const request: IdentityOnlyReviewRequest =
      accepted.kind === 'RETAIN'
        ? { ...base, identityAction: 'RETAIN' }
        : {
            ...base,
            identityAction: 'SELECT',
            ...(savedCard.reviewChecklistSelection
              ? {
                  expectedReviewChecklistSelection: {
                    checklistId: savedCard.reviewChecklistSelection.checklistId,
                    selectedAt: savedCard.reviewChecklistSelection.selectedAt,
                  },
                }
              : {}),
            ...(replaceAssignment ? { replaceChecklistAssignment: true } : {}),
            ...(accepted.kind === 'SOURCE'
              ? { checklistSourceSelection: accepted.source! }
              : {
                  manualIdentityOverrideReason: accepted.reason!,
                  manualIdentityOverrideCompleted: true,
                  ...(accepted.evidence
                    ? { manualIdentityOverrideSourceSelection: accepted.evidence }
                    : {}),
                }),
          };
    const still = () => alive.current && visit.current === at && saves.current === token;
    try {
      const result = await catalog.verifyIdentity(id, request);
      if (!still() || reads.current !== read || edits.current !== edit) return;
      if (!result.ok) {
        const body = result.body as { outcome?: unknown } | undefined;
        const unknown =
          ![400, 401, 403, 404, 409].includes(result.status) ||
          body?.outcome === 'UNKNOWN' ||
          result.message === 'identity_review_outcome_unknown';
        setUncertain(unknown);
        setMustReload(unknown || result.status === 409);
        setAccepted(null);
        setActionError(
          unknown
            ? 'The save result is uncertain. Reload the card and review its current identity before trying again.'
            : result.status === 403
              ? 'You do not have permission to verify this identity.'
              : result.status === 409
                ? 'This card changed or is protected by its listing or fulfillment workflow. Reload and review the current card.'
                : 'Identity was not saved. Review the selected source or your manual facts and reason.',
        );
        return;
      }
      if (!identityReviewResponseMatches(result.data, savedCard, request, claims?.sub)) {
        setUncertain(true);
        setMustReload(true);
        setAccepted(null);
        setActionError(
          'The save response could not be confirmed. Reload and review the current identity before trying again.',
        );
        return;
      }
      const retained = accepted.kind === 'RETAIN';
      setNotice(
        retained
          ? 'Saved identity retained. Saved drafts and local edits are unchanged.'
          : result.data.trainingCapture === 'UNKNOWN'
            ? 'Identity saved. Training capture could not be confirmed. Listing needs its separate review.'
            : 'Identity saved. Grade, photos, condition, price and listing need their separate reviews.',
      );
      setAccepted(null);
      setPendingIdentity(null);
      setMustReload(true);
      if (!retained) {
        const next = {
          ...savedCard,
          identity: result.data.identity,
          identityStatus: 'IDENTIFIED' as const,
          ...(result.data.checklistSourceLineage
            ? { checklistSourceLineage: result.data.checklistSourceLineage }
            : { checklistSourceLineage: undefined }),
          ...(result.data.manualIdentityOverride
            ? { manualIdentityOverride: result.data.manualIdentityOverride }
            : { manualIdentityOverride: undefined }),
          identityReviewDigest: undefined,
        };
        current.current = next;
        setCard(next);
        dirty.current = false;
        setDraft(result.data.identity);
      }
      const reload = reads.current + 1;
      const loaded = await load(true);
      if (!still() || reads.current !== reload) return;
      if (!loaded)
        setActionError(
          retained
            ? 'Saved identity retained; reload is unavailable. Your drafts remain here.'
            : 'Identity saved; reload is unavailable. Your saved identity is shown. Reload before another verification.',
        );
    } catch {
      if (still() && reads.current === read && edits.current === edit) {
        setUncertain(true);
        setMustReload(true);
        setAccepted(null);
        setActionError(
          'The save result is uncertain. Reload the card and review its current identity before trying again.',
        );
      }
    } finally {
      if (still() && saveLatch.current === latch) {
        saveLatch.current = null;
        setSaving(false);
      }
    }
  };
  if (!card || card.cardId !== id)
    return error ? (
      <p role="alert">
        {error} <button onClick={() => void load(true)}>Retry loading card</button>
      </p>
    ) : (
      <p>Loading…</p>
    );

  const isIdentified = card.identityStatus === 'IDENTIFIED' && card.identity !== undefined;
  const raw = card as unknown as Record<string, unknown>;
  const protectedIdentity =
    [
      'retiredAt',
      'buyerReturnHoldId',
      'returnHold',
      'tombstonedAt',
      'ownerCardId',
      'marketplaceCommerceState',
      'marketplaceCommerceUpdatedAt',
    ].some((key) => raw[key] !== undefined) ||
    (raw.commerceState !== undefined && raw.commerceState !== 'UNLISTED') ||
    !!card.marketplaceSource?.externalListingId;
  const heading = isIdentified
    ? formatIdentity(card.identity as CardIdentity)
    : 'Unidentified card';

  return (
    <div>
      {/* Header row: card identity/meta on the left, the QR label tile pinned to
          the top-right (replaces the old "Card label" section). The tile shows
          Save PDF + Print to its right and is hidden when the label hasn't
          loaded / failed. */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 240, flex: '1 1 auto' }}>
          <h2 style={{ marginTop: 0, marginBottom: 4 }}>{heading}</h2>
          <p style={{ marginTop: 0 }}>
            <IdentityStatusBadge status={card.identityStatus} />
          </p>
          <p
            className="tcvs-mono tcvs-muted"
            style={{ margin: '2px 0', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <span>Card ID: {card.cardId}</span>
            <CopyIdButton value={card.cardId} label="Copy card ID" />
          </p>
          <p>Created: {shortDateTimeWithYear(card.createdAt)}</p>
          <p>Source: {card.source}</p>
          {card.placementBatchId !== undefined && card.placementBatchId !== '' ? (
            <p>
              <Link
                className="tcvs-linklike"
                to={batchReviewHref(card.placementBatchId, card.cardId)}
              >
                Review this card
              </Link>
            </p>
          ) : null}
          {/* External listing-service SKU (sku-write-back) — shown only when set. */}
          {card.sku !== undefined && card.sku !== '' ? (
            <p>
              SKU: <code className="tcvs-mono">{card.sku}</code>
            </p>
          ) : null}
        </div>
        {label !== null ? (
          <LabelTile
            token={label.token}
            entityId={label.cardId}
            {...(label.shortCode !== undefined ? { shortCode: label.shortCode } : {})}
            qrMaxWidth={65}
            containerStyle={{
              border: '2px dashed #333',
              padding: 12,
              display: 'inline-grid',
              textAlign: 'center',
            }}
          >
            <button type="button" onClick={printSingleLabel}>
              Print
            </button>
          </LabelTile>
        ) : null}
      </div>

      {/* Physical storage location (Shelf → Box → Batch), surfaced up top. */}
      <LocationBlock card={card} />

      <PlacementActions card={card} onChanged={refresh} />

      {isAdmin ? <CardRetirementActions card={card} onChanged={refresh} /> : null}

      <VersionedImageReviewStatus {...imageReview} />
      <CardImages
        card={card.pendingImageReplacement === undefined ? (imageReview.card ?? card) : card}
        heading={heading}
        canReplace={canEdit}
        onChanged={async () => {
          imageReview.reload();
          await refresh();
        }}
      />

      {canEdit && card.pendingImageReplacement !== undefined ? (
        <PendingImageReplacementReview
          key={`replacement-${card.cardId}`}
          card={card}
          review={imageReview}
          onChanged={refresh}
        />
      ) : null}

      {canEdit ? (
        <ImageApprovalReview
          key={`approval-${card.cardId}`}
          card={card}
          review={imageReview}
          onChanged={refresh}
        />
      ) : null}

      <CustomerPhotoPanel
        card={card.cardId === id ? card : null}
        catalog={catalog}
        canReview={canEdit}
        operatorId={claims?.sub}
      />

      {canEdit ? (
        <CardResearchPricing
          card={card}
          identity={draft ?? initialDraft(card)}
          onChanged={refresh}
        />
      ) : null}

      {isIdentified ? (
        <VerifiedIdentity card={card} />
      ) : (
        <p className="tcvs-muted">
          This card has not been reviewed. No verified identity is set; the suggestions below are
          unverified.
        </p>
      )}

      {!isIdentified && card.csvSuggestion !== undefined ? (
        <CsvSuggestionBlock identity={card.csvSuggestion} />
      ) : null}

      {/* Non-authoritative OCR-derived identity suggestion (ocr-identity-extraction).
          Rendered only for an unreviewed card that carries an `ocrSuggestion`;
          absence renders nothing (Req 9.2) and it is never presented as the
          verified identity (Req 9.3, 9.6). */}
      {!isIdentified && card.ocrSuggestion !== undefined ? (
        <OcrSuggestionBlock identity={card.ocrSuggestion} />
      ) : null}

      {/* Non-authoritative manufacturer-checklist identity suggestion
          (checklist-reference). Rendered only for an unreviewed card that
          carries a `checklistSuggestion`; absence renders nothing and it is
          never presented as the verified identity (Req 11.1, 11.6). */}
      {!isIdentified && card.checklistSuggestion !== undefined ? (
        <ChecklistSuggestionBlock identity={card.checklistSuggestion} />
      ) : null}

      {/* Non-authoritative machine-read text (card-ocr-metadata). Renders only
          when OCR has completed for at least one side; absence is a valid state
          (Req 6.4, 7.2) and it is never presented as the verified identity. */}
      {card.ocr !== undefined ? <OcrBlock ocr={card.ocr} /> : null}

      {canEdit ? (
        <section aria-label="Identity review">
          <h3>Review identity</h3>
          <p>
            Verify identity only. Grade, photos, condition, price and listing need their separate
            reviews. A prepared listing must be reviewed again after identity changes.
          </p>
          {actionError ? <p role="alert">{actionError}</p> : null}
          {notice ? <p role="status">{notice}</p> : null}
          {selectionError ? <p role="alert">{selectionError}</p> : null}
          {showVariant && variantObservation && variantChoice ? (
            <div>
              <p>
                Catalog choice being reviewed; identity changes still need your existing acceptance
                and save.
              </p>
              <ChecklistVariantWarning
                entry={variantChoice.entry}
                definitions={variantObservation.definitions}
                selectedSource={variantSource}
                current={variantAssociation}
                loaded={{
                  ...variantAssociation,
                  visitId: String(variantObservation.owner),
                  requestId: String(variantObservation.request),
                  checklistId: variantObservation.checklistId,
                }}
              />
            </div>
          ) : null}
          {card.identityReviewDraft ? (
            <p role="note">
              A saved review draft is pending. It is not verified authority. Retaining the saved
              identity leaves that draft unchanged.
            </p>
          ) : null}
          {protectedIdentity ? (
            <p role="note">
              This card has listing, fulfillment or return history. Use its protected workflow; this
              page cannot verify a replacement identity.
            </p>
          ) : null}
          {!isReviewDigest(card.identityReviewDigest) ? (
            <p role="note">Reload the current card before verifying identity.</p>
          ) : null}
          <button type="button" disabled={saving} onClick={() => void load(true)}>
            Reload current card
          </button>
          {mustReload && !saving ? (
            <p role="note">Reload and review the current card before verifying again.</p>
          ) : null}
          <fieldset disabled={saving} style={{ border: 0, padding: 0 }}>
            <section aria-label="Browse current checklist product">
              <label>
                Find current checklist product
                <input
                  aria-label="Find current checklist product"
                  value={productQuery}
                  onChange={(event) => {
                    setProductQuery(event.target.value);
                    setProductSelection('');
                    browsedChecklist.current = undefined;
                    setPrintedNumber('');
                    setProducts([]);
                    clearSourceChoices();
                  }}
                />
              </label>
              <button
                type="button"
                disabled={sourceBusy || productSearch.loading}
                onClick={findProducts}
              >
                {productSearch.loading ? 'Loading checklist products…' : 'Find checklist products'}
              </button>
              {submittedProductQuery !== null ? (
                <>
                  {productSearch.loading ? (
                    <p role="status">Searching checklist products…</p>
                  ) : null}
                  {productSearch.error ? (
                    <div role="alert">
                      <p>{productSearch.error} Your local review is unchanged.</p>
                      <button
                        type="button"
                        disabled={productSearch.loading}
                        onClick={productSearch.retry}
                      >
                        Retry
                      </button>
                    </div>
                  ) : productSearch.hasMore ? (
                    <button
                      type="button"
                      disabled={productSearch.loading}
                      onClick={productSearch.loadMore}
                    >
                      Load more
                    </button>
                  ) : !productSearch.loading && products.length === 0 ? (
                    <p>
                      No current checklist product matched. Refine the search or use explicit manual
                      review.
                    </p>
                  ) : null}
                </>
              ) : null}
              {products.length ? (
                <label>
                  Checklist product
                  <select
                    aria-label="Checklist product"
                    value={productSelection}
                    disabled={sourceBusy}
                    onChange={(event) => {
                      const product = products.find(
                        (item) => item.checklistId === event.target.value,
                      );
                      if (product) {
                        setPrintedNumber('');
                        void browse(product.checklistId);
                      }
                    }}
                  >
                    <option value="" disabled>
                      Select a current product…
                    </option>
                    {products.map((product) => (
                      <option key={product.checklistId} value={product.checklistId}>
                        {product.category} · {product.product.year} {product.product.brand}{' '}
                        {product.product.product}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              <label>
                Printed card number
                <input
                  aria-label="Printed card number"
                  value={printedNumber}
                  disabled={!productSelection}
                  onChange={(event) => {
                    setPrintedNumber(event.target.value);
                    clearSourceChoices();
                  }}
                />
              </label>
              <button
                type="button"
                disabled={!productSelection || sourceBusy}
                onClick={lookupPrintedNumber}
              >
                Look up printed number
              </button>
              <p>
                A printed number is a search hint only. Review and accept the exact source below.
              </p>
              <p>
                Browsing a product does not assign it or verify identity. Choose an exact card and
                review its facts below.
              </p>
            </section>
            {!isIdentified ? <ChecklistParallelFallback match={parallelMatch} /> : null}
            {parallelDefinitions.some(
              (definition) =>
                definition.applicabilityConfidence === 'unknown' &&
                definition.definitionId ===
                  (pendingIdentity ?? accepted)?.source?.parallel?.definitionId,
            ) ? (
              <p role="note">
                Applicability unverified. Confirm this physical card shows the selected treatment.
                This identity review does not confirm applicability for the whole set.
              </p>
            ) : null}
            {pendingIdentity ? (
              <section aria-label="Review proposed checklist identity">
                <ChecklistSelectionChanges
                  beforeCard={card.identity ?? null}
                  beforeDraft={card.identityReviewDraft?.identity ?? null}
                  beforeLocal={draft ?? undefined}
                  proposed={pendingIdentity.identity}
                />
                <p>
                  Saved authority:{' '}
                  {card.manualIdentityOverride
                    ? `manual review — ${card.manualIdentityOverride.reason}`
                    : card.checklistSourceLineage?.status === 'VERIFIED'
                      ? 'verified checklist source'
                      : isIdentified
                        ? 'saved verified identity'
                        : 'not verified'}
                  .
                </p>
                <p>
                  {pendingIdentity.kind === 'SOURCE'
                    ? 'Proposed authority: this exact current checklist row and treatment.'
                    : pendingIdentity.kind === 'MANUAL'
                      ? `Proposed authority: your manual review. Reason: ${pendingIdentity.reason}`
                      : 'Retain the exact saved authority. Saved review draft and current local edits stay unchanged.'}
                </p>
                {pendingIdentity.kind === 'MANUAL' ? (
                  <p>
                    No verified checklist lineage will be asserted.
                    {pendingIdentity.evidence
                      ? ' Recorded unresolved source is retained as historical evidence only.'
                      : ''}
                  </p>
                ) : null}
                {card.reviewChecklistSelection && pendingIdentity.kind !== 'RETAIN' ? (
                  <>
                    <p>
                      {pendingRequiresReplacement
                        ? pendingIdentity.kind === 'MANUAL'
                          ? 'This removes the pending checklist assignment and establishes your manual authority.'
                          : 'This replaces the current checklist assignment with the chosen source and hierarchy.'
                        : 'This reviews the current checklist assignment. A different hierarchy needs explicit replacement.'}
                    </p>
                    <label>
                      <input
                        type="checkbox"
                        checked={replaceAssignment}
                        onChange={(event) => {
                          setReplaceAssignment(event.target.checked);
                          setIdentityAccepted(false);
                        }}
                      />
                      Replace the current checklist assignment with this reviewed choice
                    </label>
                  </>
                ) : null}
                <label>
                  <input
                    type="checkbox"
                    checked={identityAccepted}
                    onChange={(event) => setIdentityAccepted(event.target.checked)}
                  />
                  I reviewed all changed and removed identity facts and the authority and assignment
                  effects.
                </label>
                <button
                  type="button"
                  disabled={
                    !identityAccepted ||
                    !isReviewDigest(card.identityReviewDigest) ||
                    !proposalCurrent ||
                    (pendingRequiresReplacement && !replaceAssignment)
                  }
                  onClick={() => {
                    if (
                      !pendingIdentity ||
                      !proposalCurrent ||
                      !identityAccepted ||
                      (pendingRequiresReplacement && !replaceAssignment) ||
                      !isReviewDigest(card.identityReviewDigest)
                    )
                      return;
                    const next =
                      pendingIdentity.kind === 'RETAIN' ? draft : pendingIdentity.identity;
                    if (pendingIdentity.kind !== 'RETAIN') {
                      edits.current++;
                      dirty.current = true;
                      setDraft(pendingIdentity.identity);
                    }
                    setAccepted({
                      ...pendingIdentity,
                      digest: card.identityReviewDigest,
                      local: JSON.stringify(next),
                    });
                    setEditing(true);
                    setManual(pendingIdentity.kind === 'MANUAL');
                    setPendingIdentity(null);
                    setIdentityAccepted(false);
                  }}
                >
                  {pendingIdentity.kind === 'SOURCE'
                    ? 'Use this checklist identity'
                    : pendingIdentity.kind === 'MANUAL'
                      ? 'Use this manual identity'
                      : 'Use saved authority'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPendingIdentity(null);
                    setIdentityAccepted(false);
                  }}
                >
                  Cancel identity changes
                </button>
              </section>
            ) : null}
            {editing && draft ? (
              <>
                <IdentityForm value={draft} onChange={changeDraft} disabled={saving} />
                {manual ? (
                  <section aria-label="Manual identity review">
                    <label>
                      Manual review reason
                      <textarea
                        aria-label="Manual review reason"
                        value={reason}
                        maxLength={500}
                        onChange={(event) => {
                          setReason(event.target.value);
                          invalidate();
                        }}
                      />
                    </label>
                    <p>
                      Explain your physical-card review. A manual identity has no verified checklist
                      lineage.
                    </p>
                    {evidence ? (
                      <label>
                        <input
                          type="checkbox"
                          checked={includeEvidence}
                          onChange={(event) => {
                            setIncludeEvidence(event.target.checked);
                            invalidate();
                          }}
                        />
                        Keep the recorded unresolved source as historical evidence, not verified
                        lineage
                      </label>
                    ) : null}
                    <label>
                      <input
                        type="checkbox"
                        checked={completed}
                        onChange={(event) => {
                          setCompleted(event.target.checked);
                          invalidate();
                        }}
                      />
                      I checked the physical identity and completed any unresolved classification
                    </label>
                    <button
                      type="button"
                      disabled={
                        reason.trim().length < 5 ||
                        reason.trim().length > 500 ||
                        !completed ||
                        mustReload ||
                        uncertain
                      }
                      onClick={() =>
                        stage({
                          kind: 'MANUAL',
                          identity: draft,
                          reason: reason.trim(),
                          completed: true,
                          ...(includeEvidence && evidence ? { evidence: evidence.selection } : {}),
                        })
                      }
                    >
                      Review manual identity
                    </button>
                  </section>
                ) : null}
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button
                    type="button"
                    disabled={
                      !accepted ||
                      mustReload ||
                      uncertain ||
                      protectedIdentity ||
                      !isReviewDigest(card.identityReviewDigest) ||
                      saving
                    }
                    onClick={() => void onSave()}
                  >
                    {saving ? 'Verifying identity…' : 'Verify identity only'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSubmittedProductQuery(null);
                      setEditing(false);
                      setManual(false);
                      invalidate();
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSubmittedProductQuery(null);
                      setEditing(false);
                      setManual(false);
                      invalidate();
                    }}
                  >
                    Choose checklist source
                  </button>
                  {!manual ? (
                    <button type="button" onClick={enterManual}>
                      Enter manually
                    </button>
                  ) : null}
                </div>
              </>
            ) : (
              <div onChangeCapture={invalidate}>
                {candidateGroups.length > 0 ? (
                  <ChecklistGroupMenu
                    groups={candidateGroups}
                    onSelect={(entry) => stageSource(entry)}
                    onManual={enterManual}
                  />
                ) : parallelMatch?.tier === 2 && parallelDefinitions.length > 0 && draft ? (
                  <SubsetFallbackMenu
                    subset={parallelMatch.subset}
                    definitions={parallelDefinitions}
                    onSelect={() =>
                      setSelectionError(
                        'No exact roster row is available. Review these physical facts manually.',
                      )
                    }
                    onManual={enterManual}
                  />
                ) : candidates.length > 0 ? (
                  <StructuredChecklistMenu
                    candidates={candidates}
                    definitions={parallelDefinitions}
                    onSelect={stageSource}
                    onManual={enterManual}
                  />
                ) : (
                  <button type="button" onClick={enterManual}>
                    {isIdentified ? 'Review and change identity' : 'Review and set identity'}
                  </button>
                )}
              </div>
            )}
            {isIdentified && !card.reviewChecklistSelection?.requiresReview ? (
              <button
                type="button"
                onClick={() => stage({ kind: 'RETAIN', identity: card.identity! })}
              >
                Retain saved identity
              </button>
            ) : null}
          </fieldset>
        </section>
      ) : null}
    </div>
  );
}

function SubsetFallbackMenu({
  subset,
  definitions,
  onSelect,
  onManual,
}: {
  subset: string;
  definitions: ChecklistParallelDefinition[];
  onSelect: (definition?: ChecklistParallelDefinition) => void;
  onManual: () => void;
}): JSX.Element {
  return (
    <div className="tcvs-card">
      <p>
        <strong>Set/subset:</strong> {subset}
      </p>
      <label style={{ display: 'grid', gap: 4 }}>
        <span>Parallel</span>
        <select
          defaultValue=""
          onChange={(event) => {
            const definition = definitions.find((item) => item.definitionId === event.target.value);
            if (definition !== undefined) onSelect(definition);
          }}
        >
          <option value="" disabled>
            Select parallel...
          </option>
          {definitions.map((definition) => (
            <option key={definition.definitionId} value={definition.definitionId}>
              {parallelDefinitionLabel(definition)}
            </option>
          ))}
        </select>
      </label>
      <div className="tcvs-actions" style={{ marginTop: 10 }}>
        <button
          type="button"
          disabled
          title="No roster row: research only; use explicit manual review"
          onClick={() => onSelect()}
        >
          No parallel
        </button>
        <button type="button" className="tcvs-btn-secondary" onClick={onManual}>
          Enter manually
        </button>
      </div>
    </div>
  );
}

function StructuredChecklistMenu({
  candidates,
  definitions,
  onSelect,
  onManual,
}: {
  candidates: ChecklistEntry[];
  definitions: ChecklistParallelDefinition[];
  onSelect: (entry: ChecklistEntry, definition?: ChecklistParallelDefinition) => void;
  onManual: () => void;
}): JSX.Element {
  const [selectedIndex, setSelectedIndex] = useState(candidates.length === 1 ? '0' : '');
  const selected = selectedIndex === '' ? undefined : candidates[Number(selectedIndex)];
  const applicable =
    selected === undefined
      ? []
      : applicableParallelDefinitions(selected, definitions, DETAIL_CHECKLIST_SELECTION);

  return (
    <div className="tcvs-card">
      <h4 style={{ marginTop: 0 }}>Checklist identity</h4>
      <p className="tcvs-muted" style={{ marginTop: 0 }}>
        Select the card&apos;s set or subset, then choose a parallel only when the physical card
        shows that treatment.
      </p>
      {candidates.length === 1 && selected !== undefined ? (
        <p>
          <strong>Set/subset:</strong> {checklistRosterSubset(selected)}
        </p>
      ) : (
        <label style={{ display: 'grid', gap: 4 }}>
          <span>Set/subset</span>
          <select value={selectedIndex} onChange={(event) => setSelectedIndex(event.target.value)}>
            <option value="" disabled>
              Select set/subset...
            </option>
            {candidates.map((entry, index) => (
              <option key={parallelKey(entry, index)} value={index}>
                {checklistRosterSubset(entry)}
              </option>
            ))}
          </select>
        </label>
      )}
      {checklistSelectionConflictMessages(selected, definitions, DETAIL_CHECKLIST_SELECTION).map(
        (message) => (
          <p role="note" key={message}>
            {message}
          </p>
        ),
      )}
      {selected !== undefined ? (
        <label style={{ display: 'grid', gap: 4, marginTop: 10 }}>
          <span>Parallel</span>
          <select
            defaultValue=""
            onChange={(event) => {
              const definition = applicable.find(
                (item) => item.definitionId === event.target.value,
              );
              if (definition !== undefined) onSelect(selected, definition);
            }}
          >
            <option value="" disabled>
              Select parallel...
            </option>
            {applicable.map((definition) => (
              <option key={definition.definitionId} value={definition.definitionId}>
                {parallelSelectionOptions(applicable, selected, DETAIL_CHECKLIST_SELECTION).find(
                  (option) => option.value === definition.definitionId,
                )?.label ?? parallelDefinitionLabel(definition)}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <div className="tcvs-actions" style={{ marginTop: 10 }}>
        <button
          type="button"
          disabled={!checklistPlainChoiceAllowed(selected)}
          onClick={() => {
            if (selected !== undefined) onSelect(selected);
          }}
        >
          No parallel
        </button>
        <button type="button" className="tcvs-btn-secondary" onClick={onManual}>
          Enter manually
        </button>
      </div>
    </div>
  );
}

function CardRetirementActions({
  card,
  onChanged,
}: {
  card: CatalogDetailResponse;
  onChanged: () => void;
}): JSX.Element {
  const catalog = useCatalogApi();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const retire = async () => {
    const reason = window.prompt('Reason for retiring this card:');
    if (reason === null) return;
    if (reason.trim() === '') {
      setError('A retirement reason is required.');
      return;
    }
    if (
      !window.confirm(
        'Retire this card? It will be removed from active inventory, reviews, and listing eligibility.',
      )
    )
      return;
    setBusy(true);
    setError(null);
    const res = await catalog.retire(card.cardId, reason.trim());
    setBusy(false);
    if (!res.ok) {
      setError(res.message ?? res.error);
      return;
    }
    onChanged();
  };
  const restore = async () => {
    if (
      !window.confirm(
        'Restore this card record? You will need to put it away again before it returns to inventory.',
      )
    )
      return;
    setBusy(true);
    setError(null);
    const res = await catalog.restore(card.cardId);
    setBusy(false);
    if (!res.ok) {
      setError(res.message ?? res.error);
      return;
    }
    onChanged();
  };
  if (card.retiredAt !== undefined) {
    return (
      <section className="tcvs-card" style={{ marginTop: 12 }}>
        <strong className="tcvs-tone-pulled">Retired</strong>{' '}
        <span className="tcvs-muted">
          {shortDateTimeWithYear(card.retiredAt)}
          {card.retiredReason ? ` — ${card.retiredReason}` : ''}
        </span>
        <div style={{ marginTop: 8 }}>
          <button type="button" onClick={() => void restore()} disabled={busy}>
            Restore card
          </button>
        </div>
        {error !== null ? <p className="tcvs-error">{error}</p> : null}
      </section>
    );
  }
  return (
    <section style={{ marginTop: 12 }}>
      <button
        type="button"
        className="tcvs-btn-danger tcvs-btn-compact"
        onClick={() => void retire()}
        disabled={busy}
      >
        Retire card
      </button>
      {error !== null ? <p className="tcvs-error">{error}</p> : null}
    </section>
  );
}

export function imageQualityReviewLabel(status?: ImageQualityStatus): string {
  switch (status) {
    case 'ACCEPTED':
      return 'Approved';
    case 'REJECTED':
      return 'Rejected';
    case 'PENDING':
      return 'Pending review';
    default:
      return 'Not reviewed';
  }
}

/**
 * Image QA on the same card surface as authoritative identity/grade review.
 * Decisions reuse the existing image-quality endpoint and remain independent
 * from identity, grade, placement, SKU, and image-reference updates.
 */
function ImageApprovalReview({
  card,
  review,
  onChanged,
}: {
  card: CatalogDetailResponse;
  review: ReturnType<typeof useVersionedImageReview>;
  onChanged: () => void | Promise<void>;
}): JSX.Element {
  const catalog = useCatalogApi();
  const [busyAction, setBusyAction] = useState<'approve' | 'reject' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const status = card.imageQualityStatus;

  const updateStatus = async (next: 'ACCEPTED' | 'REJECTED'): Promise<void> => {
    if (
      busyAction !== null ||
      status === next ||
      (next === 'ACCEPTED' &&
        (!review.ready || (review.required && card.pendingImageReplacement !== undefined)))
    )
      return;
    if (
      next === 'REJECTED' &&
      !window.confirm(
        'Reject these card images? The card will remain in the system but will not be eligible for marketplace listing.',
      )
    ) {
      return;
    }

    setActionError(null);
    setBusyAction(next === 'ACCEPTED' ? 'approve' : 'reject');
    const result = await catalog.updateImageQuality(
      card.cardId,
      next,
      next === 'REJECTED' ? 'Rejected during identity review' : undefined,
      ...(next === 'ACCEPTED' && review.reviewId !== undefined ? [review.reviewId] : []),
    );
    review.reload();
    setBusyAction(null);
    if (!result.ok) {
      setActionError(result.message ?? result.error);
      return;
    }
    await onChanged();
  };

  const toneClass =
    status === 'ACCEPTED'
      ? 'tcvs-tone-stored'
      : status === 'REJECTED'
        ? 'tcvs-tone-pulled'
        : 'tcvs-tone-unplaced';

  return (
    <section className="tcvs-card">
      <h3 style={{ marginTop: 0 }}>Image approval</h3>
      <p style={{ marginTop: 0, marginBottom: 8 }}>
        Status: <strong className={toneClass}>{imageQualityReviewLabel(status)}</strong>
        {card.imageQualityReviewedAt !== undefined ? (
          <span className="tcvs-muted">
            {' '}
            · reviewed {shortDateTimeWithYear(card.imageQualityReviewedAt)}
          </span>
        ) : null}
      </p>
      {status === 'REJECTED' && card.imageQualityRejectedReason !== undefined ? (
        <p className="tcvs-muted" style={{ marginTop: 0 }}>
          Reason: {card.imageQualityRejectedReason}
        </p>
      ) : null}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="tcvs-btn-compact"
          disabled={
            busyAction !== null ||
            status === 'ACCEPTED' ||
            !review.ready ||
            (review.required && card.pendingImageReplacement !== undefined)
          }
          onClick={() => void updateStatus('ACCEPTED')}
        >
          {busyAction === 'approve' ? 'Approving...' : 'Approve images'}
        </button>
        <button
          type="button"
          className="tcvs-btn-danger tcvs-btn-compact"
          disabled={busyAction !== null || status === 'REJECTED'}
          onClick={() => void updateStatus('REJECTED')}
        >
          {busyAction === 'reject' ? 'Rejecting...' : 'Reject images'}
        </button>
      </div>
      {actionError !== null ? (
        <p className="tcvs-error" style={{ marginBottom: 0 }}>
          Image approval failed: {actionError}
        </p>
      ) : null}
    </section>
  );
}

/**
 * Render each present side of the card as a labeled `Front`/`Back` image,
 * driven by the per-side presigned URLs in `card.imageUrls` (Requirements 7.1,
 * 7.2, 7.5). Only present sides render — no broken/placeholder image is shown
 * for an absent side (Requirement 7.3). Falls back to the single legacy
 * `card.imageUrl` when `imageUrls` is absent (legacy responses).
 */
function CardImages({
  card,
  heading,
  canReplace,
  onChanged,
}: {
  card: CatalogDetailResponse;
  heading: string;
  canReplace: boolean;
  onChanged: () => void | Promise<void>;
}): JSX.Element | null {
  const catalog = useCatalogApi();
  const [busySide, setBusySide] = useState<'front' | 'back' | null>(null);
  const [replaceError, setReplaceError] = useState<string | null>(null);
  const [popoutOpen, setPopoutOpen] = useState(false);
  const [popoutWindow, setPopoutWindow] = useState<Window | null>(null);
  const popoutWindowRef = useRef<Window | null>(null);
  const sides: ReadonlyArray<{ side: 'front' | 'back'; label: string; url: string }> = (
    [
      { side: 'front', label: 'Front' },
      { side: 'back', label: 'Back' },
    ] as const
  ).flatMap(({ side, label }) => {
    const url = card.imageUrls?.[side];
    return url !== undefined ? [{ side, label, url }] : [];
  });
  const displaySides: ReadonlyArray<{ side?: 'front' | 'back'; label: string; url: string }> =
    sides.length > 0
      ? sides
      : card.imageUrl === undefined
        ? []
        : [{ label: 'Image', url: card.imageUrl }];

  const closePopout = useCallback(() => {
    setPopoutOpen(false);
    if (popoutWindowRef.current !== null && !popoutWindowRef.current.closed) {
      popoutWindowRef.current.close();
    }
    popoutWindowRef.current = null;
    setPopoutWindow(null);
  }, []);

  const openPopout = useCallback(() => {
    const popup = window.open('', 'tcvs-card-image-popout', 'popup=yes,width=1440,height=980');
    if (popup === null) {
      setPopoutWindow(null);
      setPopoutOpen(true);
      return;
    }
    prepareCardImagePopoutWindow(popup, heading);
    popoutWindowRef.current = popup;
    setPopoutWindow(popup);
    setPopoutOpen(true);
  }, [heading]);

  useEffect(() => {
    if (!popoutOpen || popoutWindow !== null) return;

    const previousOverflow = document.body.style.overflow;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closePopout();
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [closePopout, popoutOpen, popoutWindow]);

  useEffect(() => () => closePopout(), [closePopout]);

  if (displaySides.length === 0) return null;

  const onReplace = async (side: 'front' | 'back', file: File | undefined) => {
    if (file === undefined) return;
    setReplaceError(null);
    setBusySide(side);
    try {
      const upload = await catalog.createImageReplacementUpload(card.cardId, side, file);
      if (!upload.ok) {
        setReplaceError(upload.message ?? upload.error);
        return;
      }
      const form = new FormData();
      for (const [key, value] of Object.entries(upload.data.uploadUrl.fields)) {
        form.append(key, value);
      }
      form.append('file', file);
      const s3Res = await fetch(upload.data.uploadUrl.url, { method: 'POST', body: form });
      if (!s3Res.ok) {
        setReplaceError(`upload_failed_${s3Res.status}`);
        return;
      }
      const replaced = await catalog.replaceImage(card.cardId, side, upload.data.s3Key);
      if (!replaced.ok) {
        setReplaceError(replaced.message ?? replaced.error);
        return;
      }
      await onChanged();
    } finally {
      setBusySide(null);
    }
  };

  return (
    <>
      <div className="tcvs-card-image-toolbar">
        <button
          type="button"
          className="tcvs-icon-button"
          onClick={openPopout}
          title="Pop out card images"
          aria-label="Pop out card images full screen"
        >
          <span className="tcvs-popout-icon" aria-hidden="true" />
        </button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        {displaySides.map(({ side, label, url }) => (
          <figure key={side ?? 'legacy'} className="tcvs-card-image-figure">
            <img className="tcvs-card-image" src={url} alt={`${heading} (${label})`} />
            <figcaption
              className="tcvs-muted"
              style={{ display: 'flex', alignItems: 'center', gap: 10 }}
            >
              <span>{label}</span>
              {canReplace && side !== undefined ? (
                <label
                  className="tcvs-row-action"
                  style={{ marginLeft: 0, cursor: busySide === null ? 'pointer' : 'not-allowed' }}
                >
                  {busySide === side ? 'Replacing...' : `Replace ${side}`}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    disabled={busySide !== null}
                    hidden
                    onChange={(e) => {
                      const file = e.currentTarget.files?.[0];
                      e.currentTarget.value = '';
                      void onReplace(side, file);
                    }}
                  />
                </label>
              ) : null}
            </figcaption>
          </figure>
        ))}
      </div>
      {popoutOpen && popoutWindow === null ? (
        <CardImagePopout heading={heading} sides={displaySides} onClose={closePopout} />
      ) : null}
      {popoutOpen && popoutWindow !== null ? (
        <CardImagePopoutWindow
          popup={popoutWindow}
          heading={heading}
          sides={displaySides}
          onClose={closePopout}
        />
      ) : null}
      {replaceError !== null ? (
        <p className="tcvs-error" style={{ marginTop: 8 }}>
          Image replacement failed: {replaceError}
        </p>
      ) : null}
    </>
  );
}

function PendingImageReplacementReview({
  card,
  review,
  onChanged,
}: {
  card: CatalogDetailResponse;
  review: ReturnType<typeof useVersionedImageReview>;
  onChanged: () => void | Promise<void>;
}): JSX.Element | null {
  const catalog = useCatalogApi();
  const [busy, setBusy] = useState<'approve' | 'reject' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pending = card.pendingImageReplacement;
  if (pending === undefined) return null;

  const decide = async (decision: 'approve' | 'reject') => {
    if (busy !== null || (decision === 'approve' && !review.ready)) return;
    setBusy(decision);
    setError(null);
    try {
      const result = await catalog.decidePendingImageReplacement(
        card.cardId,
        pending.replacementId,
        decision,
        decision === 'reject' ? 'Replacement images rejected during review' : undefined,
        ...(decision === 'approve' && review.reviewId !== undefined ? [review.reviewId] : []),
      );
      review.reload();
      if (!result.ok) {
        setError(result.message ?? result.error);
        return;
      }
      await onChanged();
    } finally {
      setBusy(null);
    }
  };

  return (
    <section style={{ marginTop: 24 }}>
      <h3 style={{ marginBottom: 4 }}>Replacement images ready</h3>
      <p className="tcvs-muted" style={{ marginTop: 0 }}>
        Sent from {pending.deviceId} on {shortDateTimeWithYear(pending.submittedAt)}.{' '}
        {review.required
          ? 'Current images are shown for comparison only.'
          : 'Current images remain active until this pair is approved.'}
      </p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 16,
        }}
      >
        {(['front', 'back'] as const).map((side) => (
          <div key={side}>
            <h4 style={{ textTransform: 'capitalize', marginBottom: 8 }}>{side}</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {card.imageUrls?.[side] !== undefined ? (
                <figure style={{ margin: 0 }}>
                  <img
                    className="tcvs-card-image"
                    src={card.imageUrls[side]}
                    alt={`Current ${side}`}
                  />
                  <figcaption className="tcvs-muted">Current</figcaption>
                </figure>
              ) : null}
              {(review.required ? review.card?.imageUrls?.[side] : pending.imageUrls[side]) !==
              undefined ? (
                <figure style={{ margin: 0 }}>
                  <img
                    className="tcvs-card-image"
                    src={review.required ? review.card?.imageUrls?.[side] : pending.imageUrls[side]}
                    alt={`Replacement ${side}`}
                  />
                  <figcaption className="tcvs-muted">Replacement</figcaption>
                </figure>
              ) : null}
            </div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <button
          type="button"
          className="tcvs-btn-danger"
          disabled={busy !== null}
          onClick={() => void decide('reject')}
        >
          {busy === 'reject' ? 'Rejecting...' : 'Reject replacements'}
        </button>
        <button
          type="button"
          disabled={busy !== null || !review.ready}
          onClick={() => void decide('approve')}
        >
          {busy === 'approve' ? 'Approving...' : 'Approve replacements'}
        </button>
      </div>
      {error !== null ? <p className="tcvs-error">Error: {error}</p> : null}
    </section>
  );
}

function prepareCardImagePopoutWindow(popup: Window, heading: string): void {
  popup.document.open();
  popup.document.write(
    `<!doctype html><html><head><title>${escapeHtml(heading)} images</title></head><body></body></html>`,
  );
  popup.document.close();
  popup.document.body.style.margin = '0';
  popup.document.body.style.background = 'var(--tcvs-bg, #050617)';
  popup.document.body.style.color = 'var(--tcvs-text, #f7f8ff)';

  for (const node of Array.from(document.head.children)) {
    if (
      node instanceof HTMLLinkElement &&
      (node.rel === 'stylesheet' || node.rel === 'preload' || node.rel === 'modulepreload')
    ) {
      const clone = node.cloneNode(true) as HTMLLinkElement;
      clone.href = node.href;
      popup.document.head.appendChild(clone);
    }
    if (node instanceof HTMLStyleElement) {
      popup.document.head.appendChild(node.cloneNode(true));
    }
  }
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

function CardImagePopoutWindow({
  popup,
  heading,
  sides,
  onClose,
}: {
  popup: Window;
  heading: string;
  sides: ReadonlyArray<{ side?: 'front' | 'back'; label: string; url: string }>;
  onClose: () => void;
}): JSX.Element | null {
  useEffect(() => {
    const poll = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(poll);
        onClose();
      }
    }, 250);
    return () => window.clearInterval(poll);
  }, [popup, onClose]);

  if (popup.closed) return null;

  return createPortal(
    <CardImagePopout heading={heading} sides={sides} onClose={onClose} />,
    popup.document.body,
  );
}

function CardImagePopout({
  heading,
  sides,
  onClose,
}: {
  heading: string;
  sides: ReadonlyArray<{ side?: 'front' | 'back'; label: string; url: string }>;
  onClose: () => void;
}): JSX.Element {
  return (
    <div className="tcvs-image-approval-popout" role="dialog" aria-modal="true">
      <header className="tcvs-image-approval-popout-header">
        <div>
          <h3>{heading}</h3>
          <div className="tcvs-muted">Card images</div>
        </div>
        <div className="tcvs-image-approval-popout-nav">
          <button
            type="button"
            className="tcvs-icon-button tcvs-image-approval-popout-close"
            onClick={onClose}
            title="Close"
            aria-label="Close card image pop-out"
          >
            <span aria-hidden="true">X</span>
          </button>
        </div>
      </header>
      <main className="tcvs-image-approval-popout-main">
        <div className="tcvs-image-approval-grid tcvs-image-approval-grid-fullscreen">
          {sides.map(({ side, label, url }) => (
            <figure key={side ?? 'legacy'} className="tcvs-image-approval-side-fullscreen">
              <figcaption style={{ marginBottom: 6 }}>{label}</figcaption>
              <img
                className="tcvs-image-approval-image-fullscreen"
                src={url}
                alt={`${heading} (${label})`}
              />
            </figure>
          ))}
        </div>
      </main>
    </div>
  );
}

/**
 * Render the card's resolved physical location (Shelf → Box → Storage_Batch, or
 * a coarser state) using the same `describePlacement` helper and design-token
 * tone classes as the storage views, so the catalog detail page shows where a
 * card physically lives. Renders nothing when the API returned no `location`
 * (legacy responses).
 */
function LocationBlock({ card }: { card: CatalogDetailResponse }): JSX.Element | null {
  // Render when we have a resolved location OR just the approximate batch
  // position (a card can carry a SKU-write-back batchIndex without a location).
  if (card.location === undefined && card.batchIndex === undefined) return null;
  const display = card.location !== undefined ? describePlacement(card.location) : undefined;
  const batchPosition =
    card.location?.status === 'STORED' || card.location?.status === 'STORED_UNPLACED'
      ? card.location.batchPosition
      : undefined;
  const toneClass: Record<string, string> = {
    stored: 'tcvs-tone-stored',
    unplaced: 'tcvs-tone-unplaced',
    pulled: 'tcvs-tone-pulled',
    absent: 'tcvs-tone-absent',
    unknown: 'tcvs-tone-unknown',
  };
  return (
    <section className="tcvs-card">
      <h3 style={{ marginTop: 0 }}>Location</h3>
      <p style={{ margin: 0 }}>
        {display !== undefined ? (
          <strong className={toneClass[display.tone]}>{display.label}</strong>
        ) : null}
        {display?.showPath && display.path !== undefined ? (
          <span className="tcvs-mono" style={{ marginLeft: 8 }}>
            Shelf{' '}
            <Link to={entityDetailHref('SHELF', display.path.shelf.entityId)}>
              {display.path.shelf.shelfCode}
            </Link>{' '}
            → Box{' '}
            <Link to={entityDetailHref('BOX', display.path.box.entityId)}>
              {display.path.box.entityId}
            </Link>{' '}
            {display.path.row !== undefined ? (
              <>
                → Row{' '}
                <Link to={entityDetailHref('ROW', display.path.row.entityId)}>
                  {display.path.row.rowCode ?? display.path.row.entityId}
                </Link>{' '}
              </>
            ) : null}
            → Batch{' '}
            <Link to={entityDetailHref('BATCH', display.path.batch.entityId)}>
              {display.path.batch.entityId}
            </Link>
          </span>
        ) : null}
        {display?.batch !== undefined ? (
          <span className="tcvs-mono" style={{ marginLeft: 8 }}>
            {display.shelf !== undefined ? (
              <>
                Shelf{' '}
                <Link to={entityDetailHref('SHELF', display.shelf.entityId)}>
                  {display.shelf.shelfCode}
                </Link>{' '}
                →{' '}
              </>
            ) : null}
            {display.box !== undefined ? (
              <>
                Box{' '}
                <Link to={entityDetailHref('BOX', display.box.entityId)}>
                  {display.box.entityId}
                </Link>{' '}
                →{' '}
              </>
            ) : null}
            {display.row !== undefined ? (
              <>
                Row{' '}
                <Link to={entityDetailHref('ROW', display.row.entityId)}>
                  {display.row.rowCode ?? display.row.entityId}
                </Link>{' '}
                →{' '}
              </>
            ) : null}
            Batch{' '}
            <Link to={entityDetailHref('BATCH', display.batch.entityId)}>
              {display.batch.entityId}
            </Link>
          </span>
        ) : null}
        {batchPosition !== undefined ? (
          <span className="tcvs-muted" style={{ marginLeft: 8 }}>
            → Pick position: {batchPosition.position} of {batchPosition.total}
          </span>
        ) : null}
      </p>
    </section>
  );
}

/**
 * Pick / Restock action for the card's physical placement, reusing the existing
 * `POST /storage/pick` and `POST /storage/putaway` routes (no new backend). The
 * action offered is derived from the resolved location:
 *   - STORED / STORED_UNPLACED → a **Pick** button (pulls the card from storage);
 *   - PULLED                   → a **Restock** form (puts it back into a batch the
 *     operator names, since putaway needs a destination);
 *   - otherwise (never placed / unknown / not yet resolved) → nothing.
 * Any authenticated operator may pick/restock (matching the routes' auth); this
 * is a routine warehouse action, unlike the reviewer/admin-gated identity edit.
 */
function PlacementActions({
  card,
  onChanged,
}: {
  card: CatalogDetailResponse;
  onChanged: () => void | Promise<void>;
}): JSX.Element | null {
  const storage = useStorageApi();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [restockTarget, setRestockTarget] = useState('');

  const action = placementAction(card.location);
  if (action === 'none') return null;

  const onPick = async (): Promise<void> => {
    setError(null);
    const confirmed = window.confirm(
      `Pick ${card.cardId}? This will mark the card as pulled from storage.`,
    );
    if (!confirmed) return;
    setBusy(true);
    const res = await storage.pick(card.cardId);
    setBusy(false);
    if (!res.ok) {
      setError(res.message ?? res.error);
      return;
    }
    if (res.data.sourceBatchId !== undefined) {
      setRestockTarget(res.data.sourceBatchId);
    }
    void onChanged();
  };

  const onRestock = async (): Promise<void> => {
    const target = restockTarget.trim();
    if (target === '') {
      setError('Enter the Storage_Batch id to restock into.');
      return;
    }
    setBusy(true);
    setError(null);
    const res = await storage.putaway({ type: 'CARD', cardId: card.cardId }, target);
    setBusy(false);
    if (!res.ok) {
      setError(res.message ?? res.error);
      return;
    }
    setRestockTarget('');
    void onChanged();
  };

  return (
    <section className="tcvs-card">
      <h3 style={{ marginTop: 0 }}>Placement</h3>
      {action === 'pick' ? (
        <>
          <p className="tcvs-muted" style={{ marginTop: 0 }}>
            Pull this card from storage when you physically remove it (e.g. for a sale or shipment).
            It can be restocked later.
          </p>
          <button type="button" onClick={() => void onPick()} disabled={busy}>
            {busy ? 'Picking…' : 'Pick (pull from storage)'}
          </button>
        </>
      ) : (
        <>
          <p className="tcvs-muted" style={{ marginTop: 0 }}>
            This card is pulled. Restock it into a Storage_Batch to return it to inventory. If it
            was just picked from this page, the source batch is prefilled.
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              className="tcvs-mono"
              placeholder="Storage_Batch id"
              value={restockTarget}
              onChange={(e) => setRestockTarget(e.target.value)}
            />
            <button type="button" onClick={() => void onRestock()} disabled={busy}>
              {busy ? 'Restocking…' : 'Restock into batch'}
            </button>
          </div>
        </>
      )}
      {error !== null ? (
        <p className="tcvs-error" style={{ marginBottom: 0 }}>
          Error: {error}
        </p>
      ) : null}
    </section>
  );
}

/** Render the authoritative, human-verified identity block. */
function VerifiedIdentity({ card }: { card: CatalogDetailResponse }): JSX.Element {
  return (
    <section className="tcvs-card tcvs-card-success">
      <h3 style={{ marginTop: 0 }}>Verified identity</h3>
      <p style={{ marginBottom: 4 }}>
        <strong>Identity:</strong> {formatIdentity(card.identity as CardIdentity)}
      </p>
      <IdentityExtras identity={card.identity as CardIdentity} />
    </section>
  );
}

/** The optional listing fields, in display order (csv-identity-import). */
const IDENTITY_EXTRA_FIELDS: ReadonlyArray<{ key: keyof CardIdentity; label: string }> = [
  { key: 'year', label: 'Year' },
  { key: 'brand', label: 'Brand' },
  { key: 'subset', label: 'Subset' },
  { key: 'attributes', label: 'Attributes' },
  { key: 'team', label: 'Team' },
  { key: 'variation', label: 'Variation' },
];

/**
 * Format the print run (`serialTo`) for display (checklist-reference):
 *   - `1`                  → `"1/1"` (one-of-one);
 *   - an integer 2..999,999 → `"/N"` (serial-numbered indicator);
 *   - anything outside `[1, 999,999]`, non-integer, or absent → `undefined` (omitted).
 * Returning `undefined` lets callers simply skip the print run so the remaining
 * identity fields still render without error (Req 11.2–11.5).
 */
function formatPrintRun(serialTo: number | undefined): string | undefined {
  if (serialTo === undefined || !Number.isInteger(serialTo)) return undefined;
  if (serialTo < 1 || serialTo > 999_999) return undefined;
  return serialTo === 1 ? '1/1' : `/${serialTo}`;
}

/**
 * Render the richer listing fields of a {@link CardIdentity}
 * (csv-identity-import) plus the print run (`serialTo`, checklist-reference),
 * omitting any that are absent. Renders nothing when none are present, so it's
 * safe on a core-only identity (Req 6.1, 6.2, 11.4, 11.5).
 */
function IdentityExtras({ identity }: { identity: CardIdentity }): JSX.Element | null {
  const items: Array<{ key: string; label: string; value: string }> = IDENTITY_EXTRA_FIELDS.filter(
    ({ key }) => identity[key] !== undefined && identity[key] !== '',
  ).map(({ key, label }) => ({ key, label, value: String(identity[key]) }));

  // The print run is a number (not a string like IDENTITY_EXTRA_FIELDS), so it's
  // rendered as a dedicated item here rather than via the string-field filter.
  const printRun = formatPrintRun(identity.serialTo);
  if (printRun !== undefined) {
    items.push({ key: 'serialTo', label: 'Print run', value: printRun });
  }

  if (items.length === 0) return null;
  return (
    <p className="tcvs-muted" style={{ marginTop: 0, marginBottom: 4 }}>
      {items.map(({ key, label, value }, i) => (
        <span key={key}>
          {i > 0 ? ' · ' : ''}
          {label}: {value}
        </span>
      ))}
    </p>
  );
}

/**
 * Render the CSV-imported identity suggestion (csv-identity-import) for an
 * unreviewed card, clearly labeled as a non-authoritative suggestion. The
 * operator confirms it via the review form below (which is pre-filled from it).
 */
function CsvSuggestionBlock({ identity }: { identity: CardIdentity }): JSX.Element {
  return (
    <section className="tcvs-card">
      <h3 style={{ marginTop: 0 }}>Imported identity (suggestion)</h3>
      <p style={{ marginBottom: 4 }}>
        <strong>From listing CSV:</strong> {formatIdentity(identity)}
      </p>
      <IdentityExtras identity={identity} />
      <p className="tcvs-muted" style={{ margin: 0 }}>
        Unverified — review below to confirm it as this card&apos;s identity.
      </p>
    </section>
  );
}

/**
 * Render the OCR-derived identity suggestion (ocr-identity-extraction) for an
 * unreviewed card, clearly labeled as a non-authoritative suggestion and
 * visually separate from the authoritative identity, the ML `suggestion`, the
 * imported `csvSuggestion`, and the machine-read `OcrBlock` text (Req 9.3, 9.6).
 * The extracted `ocrSuggestion` may be partial; `formatIdentity`/`IdentityExtras`
 * simply omit absent fields. It is NEVER presented as this card's verified
 * identity — the operator confirms/corrects it via the review form below (which
 * is pre-filled from it), so authority still comes from the operator PATCH.
 */
function OcrSuggestionBlock({ identity }: { identity: CardIdentity }): JSX.Element {
  return (
    <section className="tcvs-card">
      <h3 style={{ marginTop: 0 }}>OCR-derived identity (suggestion)</h3>
      <p style={{ marginBottom: 4 }}>
        <strong>From machine-read text:</strong> {formatIdentity(identity)}
      </p>
      <IdentityExtras identity={identity} />
      <p className="tcvs-muted" style={{ margin: 0 }}>
        Unverified — review below to confirm it as this card&apos;s identity.
      </p>
    </section>
  );
}

/**
 * Render the manufacturer-checklist identity suggestion (checklist-reference)
 * for an unreviewed card, clearly labeled as a non-authoritative suggestion and
 * visually separate — its own section + heading — from the authoritative
 * identity, the ML `suggestion`, the imported `csvSuggestion`, and the
 * OCR-derived `ocrSuggestion` (Req 11.1). The `checklistSuggestion` may be
 * partial and may carry a `serialTo`; `formatIdentity`/`IdentityExtras` simply
 * omit absent fields. It is NEVER presented as this card's verified identity —
 * the operator confirms/corrects it via the review form below (which is
 * pre-filled from it), so authority still comes from the operator PATCH.
 */
function ChecklistSuggestionBlock({ identity }: { identity: CardIdentity }): JSX.Element {
  return (
    <section className="tcvs-card">
      <h3 style={{ marginTop: 0 }}>Checklist match (suggestion)</h3>
      <p style={{ marginBottom: 4 }}>
        <strong>From manufacturer checklist:</strong> {formatIdentity(identity)}
      </p>
      <IdentityExtras identity={identity} />
      <p className="tcvs-muted" style={{ margin: 0 }}>
        Unverified — review below to confirm it as this card&apos;s identity.
      </p>
    </section>
  );
}

/** A stable React key for a Parallel candidate from its Natural_Key tuple. */
function parallelKey(entry: ChecklistEntry, index: number): string {
  return `${entry.key.number}#${entry.key.cardSet ?? ''}#${index}`;
}

/**
 * Product-grouped escape hatch for a tolerant lookup that cannot prove which
 * checklist the card belongs to. A run-level checklist context prevents this
 * state for normal batch processing; it remains useful for legacy/no-run cards.
 */
function ChecklistGroupMenu({
  groups,
  onSelect,
  onManual,
}: {
  groups: ChecklistResolveGroup[];
  onSelect: (entry: ChecklistEntry) => void;
  onManual: () => void;
}): JSX.Element {
  return (
    <div className="tcvs-card">
      <h4 style={{ marginTop: 0 }}>Checklist product is uncertain</h4>
      {groups.map((group) => (
        <div key={`${group.product.year}#${group.product.brand}#${group.product.product}`}>
          <h5 style={{ margin: '12px 0 4px' }}>
            {group.product.year} {group.product.brand} {group.product.product}
          </h5>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 4 }}>
            {group.candidates.map((entry, index) => (
              <li key={parallelKey(entry, index)}>
                <button
                  type="button"
                  style={{ width: '100%', textAlign: 'left' }}
                  onClick={() => onSelect(entry)}
                >
                  {formatIdentity(entry.identity)}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
      <div style={{ marginTop: 8 }}>
        <button type="button" onClick={onManual}>
          Enter manually
        </button>
      </div>
    </div>
  );
}

/** The card sides rendered by {@link OcrBlock}, in display order. */
const OCR_SIDES: ReadonlyArray<{ key: 'front' | 'back'; label: string }> = [
  { key: 'front', label: 'Front' },
  { key: 'back', label: 'Back' },
];

/**
 * Render the non-authoritative machine-read OCR text (card-ocr-metadata) for a
 * card, clearly labeled as machine-read and visually distinct from the
 * authoritative identity/grade and from the ML `suggestion` / imported
 * `csvSuggestion` blocks. Only the sides present in `ocr` are shown; an absent
 * side renders nothing (Req 2.3, 7.4). This is a machine-read hint only and is
 * NEVER presented as the card's verified identity (Req 7.5).
 */
function OcrBlock({ ocr }: { ocr: OcrMetadata }): JSX.Element {
  const sides = OCR_SIDES.flatMap(({ key, label }) => {
    const result = ocr[key];
    return result !== undefined ? [{ key, label, result }] : [];
  });
  // Collapsed by default via a native <details> disclosure so the raw OCR text
  // takes no vertical space until an operator chooses to peek at it.
  return (
    <details className="tcvs-card tcvs-card-info">
      <summary
        className="tcvs-heading-info"
        style={{ cursor: 'pointer', fontWeight: 600, listStyle: 'revert' }}
      >
        Machine-read text (OCR)
      </summary>
      <div style={{ marginTop: 12 }}>
        {sides.map(({ key, label, result }) => (
          <OcrSideView key={key} label={label} result={result} />
        ))}
        <p className="tcvs-muted" style={{ margin: 0 }}>
          Machine-read text only — not verified and not this card&apos;s identity. Engine:{' '}
          <code className="tcvs-mono">{ocr.engine}</code>
        </p>
      </div>
    </details>
  );
}

/**
 * Render a single OCR side: its aggregate `text` (or an explicit "no text"
 * note for an empty-text side) plus the per-line text when present.
 */
function OcrSideView({ label, result }: { label: string; result: OcrSideResult }): JSX.Element {
  const text = result.text.trim();
  return (
    <div style={{ marginBottom: 8 }}>
      <p style={{ marginTop: 0, marginBottom: 4 }}>
        <strong>{label}:</strong>{' '}
        {text !== '' ? (
          <span style={{ whiteSpace: 'pre-wrap' }}>{result.text}</span>
        ) : (
          <span className="tcvs-muted">No text detected.</span>
        )}
      </p>
      {result.lines.length > 0 ? (
        <ul className="tcvs-muted" style={{ marginTop: 0, marginBottom: 0 }}>
          {result.lines.map((line: OcrTextLine, i: number) => (
            <li key={i}>{line.text}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * Seed the correction form so confirming is a one-click save. Priority
 * (checklist-reference, Req 10.1): verified → checklist → csv → ocr → ML →
 * empty — the first source present on the card wins, and absent sources fall
 * through without error:
 *   1. the verified authoritative identity (when present);
 *   2. the manufacturer-checklist suggestion (checklist-reference) — a
 *      ground-truth reference match, so it outranks the noisier sources; it may
 *      carry `serialTo` and any subset of fields, so missing core fields fill
 *      from `EMPTY_IDENTITY` (`withDefaults`) and omitted fields stay empty
 *      (Req 10.2, 10.6);
 *   3. the CSV-imported identity suggestion (csv-identity-import) — the listing
 *      service already matched the card, so it's the best pre-fill for an
 *      unreviewed card and carries the richer fields;
 *   4. the OCR-derived identity suggestion (ocr-identity-extraction) — a
 *      machine-read hint that may be partial, so its missing core fields are
 *      filled from `EMPTY_IDENTITY` before seeding the form (`withDefaults`);
 *   5. the ML identification suggestion (when known);
 *   6. an empty identity.
 * None of these are authoritative until the operator confirms (the PATCH). A
 * multi-entry Parallel menu is handled separately (the operator selects a
 * parallel before the form seeds, Req 10.3, 10.4) and never flows through here.
 */
export function initialDraft(card: CatalogDetailResponse): CardIdentity {
  if (card.identityReviewDraft?.identity !== undefined)
    return withDefaults(card.identityReviewDraft.identity);
  if (card.identity !== undefined) return card.identity;
  if (card.checklistSuggestion !== undefined) return withDefaults(card.checklistSuggestion);
  if (card.csvSuggestion !== undefined) return card.csvSuggestion;
  if (card.ocrSuggestion !== undefined) return withDefaults(card.ocrSuggestion);
  const suggestion = card.suggestion?.identification;
  if (suggestion !== undefined && suggestion.status === 'known') return suggestion.identity;
  return EMPTY_IDENTITY;
}

/**
 * Merge a possibly-partial {@link CardIdentity} onto {@link EMPTY_IDENTITY} so
 * every core field (`category`/`set`/`number`/`name`) is a defined string. The
 * OCR-derived `ocrSuggestion` may omit core fields, but `IdentityForm` renders
 * controlled inputs that need a complete shape — this guarantees it. Optional
 * listing fields (year/brand/…) carry through untouched when present.
 */
function withDefaults(identity: CardIdentity): CardIdentity {
  return { ...EMPTY_IDENTITY, ...identity };
}
